import React from 'react';

// CalmNote Privacy Policy Component
// Usage: import PrivacyPolicy from './PrivacyPolicy';
// The component injects a <style> block with the CSS below. You can also move CSS into a separate file.

const css = `
.privacy-wrapper { max-width:980px; margin:40px auto; padding:28px; font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; color:#111; }
.privacy-hero { background: linear-gradient(90deg, rgba(91,138,114,0.06), rgba(91,138,114,0.03)); padding:28px; border-radius:12px; margin-bottom:18px; }
.privacy-title { font-family: 'Playfair Display', serif; font-size:34px; color:#345b47; margin:0 0 6px; }
.privacy-sub { color:#516a5a; margin:0; font-size:15px; }
.policy-section { margin-top:22px; }
.policy-section h3 { font-size:18px; margin-bottom:8px; color:#244b3a; }
.policy-section p, .policy-section li { color:#333; line-height:1.6; font-size:15px; }
.policy-list { margin:12px 0 0 18px; }
.small { font-size:13px; color:#666; }
.footer-note { margin-top:20px; font-size:13px; color:#666; }
`;

export default function PrivacyPolicy({ companyName = 'CalmNote', effectiveDate = 'January 1, 2025', contactEmail = 'privacy@calmnoteletters.com' }) {
  return (
    <div className="privacy-wrapper" aria-labelledby="privacy-heading">
      <style>{css}</style>

      <div className="privacy-hero">
        <h1 id="privacy-heading" className="privacy-title">Privacy Policy</h1>
        <p className="privacy-sub">Effective Date: {effectiveDate}</p>
      </div>

      <div className="policy-section">
        <h3>Introduction</h3>
        <p>
          {companyName} ("we", "us", or "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and
          safeguard your information when you visit our website or use our services. By accessing our site, you agree to the terms
          outlined in this policy.
        </p>
      </div>

      <div className="policy-section">
        <h3>1. Information We Collect</h3>
        <p>We may collect the following types of information:</p>
        <ul className="policy-list">
          <li><strong>Personal Information</strong> – Information you voluntarily provide, such as name, email address, company information, and contact details.</li>
          <li><strong>Automatically Collected Data</strong> – When you access our site, we may automatically collect IP address, browser type, device information, pages viewed, visit duration, and cookie data.</li>
        </ul>
      </div>

      <div className="policy-section">
        <h3>2. How We Use Your Information</h3>
        <p>We use collected data to:</p>
        <ul className="policy-list">
          <li>Provide and maintain our services</li>
          <li>Improve website performance and user experience</li>
          <li>Communicate updates or important notices</li>
          <li>Respond to inquiries or support requests</li>
          <li>Ensure security and fraud prevention</li>
        </ul>
      </div>

      <div className="policy-section">
        <h3>3. Sharing of Information</h3>
        <p>We do <strong>not</strong> sell your information. We may share data only with:</p>
        <ul className="policy-list">
          <li>Service providers assisting in website operations (only as necessary and under contract)</li>
          <li>Legal authorities when required by law</li>
        </ul>
      </div>

      <div className="policy-section">
        <h3>4. Cookies and Tracking Technologies</h3>
        <p>
          We may use cookies, web beacons, and analytics tools to enhance functionality and analyze site traffic. You may disable cookies
          in your browser settings; however, some parts of the website may not function properly if cookies are disabled.
        </p>
      </div>

      <div className="policy-section">
        <h3>5. Data Security & Retention</h3>
        <p>
          We implement appropriate technical and organizational measures to safeguard your data. However, no online transmission is 100% secure.
          We retain information only as long as necessary to fulfill the purposes described in this policy or comply with legal obligations.
        </p>
      </div>

      <div className="policy-section">
        <h3>6. Your Rights</h3>
        <p>You may request to:</p>
        <ul className="policy-list">
          <li>Access the data we hold</li>
          <li>Correct inaccurate information</li>
          <li>Request deletion of your data</li>
          <li>Opt out of communications</li>
        </ul>
        <p>To submit a request, please contact us at: <a href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
      </div>

      <div className="policy-section">
        <h3>7. Third-Party Links</h3>
        <p>
          Our website may contain links to external sites. We are not responsible for their privacy practices. Please review their policies
          before providing personal information.
        </p>
      </div>

      <div className="policy-section">
        <h3>8. Changes to This Policy</h3>
        <p>
          We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date. Continued use of
          our website after changes indicates acceptance of the revised terms.
        </p>
      </div>

      <div className="policy-section footer-note">
        <p>If you have questions regarding this policy, contact: <a href={`mailto:${contactEmail}`}>{contactEmail}</a></p>
        <p className="small">© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
      </div>
    </div>
  );
}
